import React from 'react';
import NProgress from 'nprogress';
import { useRouter } from 'next/router';
import { useMutation } from '@apollo/client';
import { useNotify } from '../../../hooks';
import { Currency, PaymentMethod } from '../../../models';
import { validatePaymentMethodForm } from '../../../lib/formValidations/validatePaymentMethodForm';
import {
  CREATE_PAYMENT_METHOD,
  UPDATE_PAYMENT_METHOD,
} from '../../../graphql/mutation';
import FormButtons from '../../shared/Forms/FormButtons';
import PaymentMethodFormFields from './PaymentMethodFormFields';

interface PaymentMethodFormProps {
  paymentMethod?: PaymentMethod;
  currencies: Currency[];
}

function PaymentMethodForm({
  paymentMethod,
  currencies,
}: PaymentMethodFormProps) {
  const notify = useNotify();
  const router = useRouter();
  const [disabled, setDisabled] = React.useState(false);
  const [formState, setFormState] = React.useState<Partial<PaymentMethod>>(
    paymentMethod
      ? {
          ...paymentMethod,
          currency: (paymentMethod.currency as Currency)._id,
        }
      : { paymentData: [] }
  );

  const [createPaymentMethodMutation] = useMutation<{
    createPaymentMethod: {
      recordId: string;
    };
  }>(CREATE_PAYMENT_METHOD);

  const [updatePaymentMethodMutation] = useMutation<{
    updatePaymentMethod: {
      recordId: string;
    };
  }>(UPDATE_PAYMENT_METHOD);

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (disabled) return;
    try {
      NProgress.start();
      setDisabled(true);
      const error = validatePaymentMethodForm({ ...formState });
      const paymentMethodData = {
        ...formState,
        commission: Number(formState.commission),
        taxes: [],
        automated: false,
        paymentData: formState.paymentData.map((item) => ({
          ...item,
          __typename: undefined,
        })),
      };
      if (error) {
        console.log(error);
        return notify(error, 'error');
      }
      if (!paymentMethod?._id) {
        createPaymentMethod(paymentMethodData);
      } else {
        if ((paymentMethodData.currency as Currency)?._id) {
          paymentMethodData.currency = (
            paymentMethodData.currency as Currency
          )?._id;
        }
        updatePaymentMethod(paymentMethodData);
      }
    } catch (error) {
      return notify(error, 'error');
    } finally {
      NProgress.done();
      setDisabled(false);
    }
  };

  const createPaymentMethod = async (paymentData: Partial<PaymentMethod>) => {
    try {
      const { data } = await createPaymentMethodMutation({
        variables: {
          record: {
            ...paymentData,
          },
        },
      });
      if (data?.createPaymentMethod?.recordId) {
        notify('Método de pago se ha creado con éxito', 'success');
        router.push('/app/settings/payment');
      } else {
        notify('Ha ocurrido un error al crear el método de pago', 'error');
      }
    } catch (error) {
      return notify(error.message, 'error');
    }
  };

  const updatePaymentMethod = async (paymentData: Partial<PaymentMethod>) => {
    delete paymentData._id;
    try {
      const { data } = await updatePaymentMethodMutation({
        variables: {
          record: {
            ...paymentData,
            __typename: undefined,
          },
          filter: {
            _id: paymentMethod._id,
          },
        },
      });
      if (data?.updatePaymentMethod?.recordId) {
        notify('Método de pago actualizado con éxito', 'success');
        router.push('/app/settings/payment');
      } else {
        notify('Ha ocurrido un error al actualizar el método de pago', 'error');
      }
    } catch (error) {
      return notify(error.message, 'error');
    }
  };

  const toggleActive = () =>
    updatePaymentMethod({ active: !paymentMethod.active });

  return (
    <form
      method="POST"
      onSubmit={onSubmit}
      className="flex flex-col w-full space-y-10"
    >
      <PaymentMethodFormFields
        formState={formState}
        setFormState={setFormState}
        currencies={currencies}
      />
      <FormButtons
        type={paymentMethod ? 'update' : 'create'}
        toggleActive={toggleActive}
        active={paymentMethod?.active}
        disabled={disabled}
        onSubmit={onSubmit}
      />
    </form>
  );
}

export default PaymentMethodForm;
